import {
  Alert,
  AppBar,
  Avatar,
  Badge,
  Box,
  CircularProgress,
  Button,
  ButtonBase,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Paper,
  Popover,
  Slide,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { fundoNeutro } from '../../theme/superficie';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MenuIcon from '@mui/icons-material/Menu';
import StorefrontIcon from '@mui/icons-material/Storefront';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  useGetCatalogoQuery,
  useGetHorariosLojaQuery,
  useGetLojaAtualQuery,
  type BannerCardapio,
  type ConfiguracaoLoja,
  type GrupoCatalogo,
  type IdentificadorLoja,
  type ProdutoCatalogo,
} from '../../api';
import type { RootState } from '../../store';
import { canalDoIdentificador, ehDispositivoFixo, ehVitrine } from '../../lib/canal';
import { carrinhoEmDia } from '../../lib/reconciliar-carrinho';
import { contextoSalao } from '../../lib/salao';
import { onAbrirPromocoes } from '../../lib/tablet-eventos';
import { capitalize, formatarBRL, normalizarTexto } from '../../lib/formato';
import { PrecoProduto } from '../../components/preco-produto';
import { urlImagemProduto } from '../../lib/imagem';
import { BadgeAvaliacoes } from '../../components/badge-avaliacoes';
import { CapaLoja } from '../../components/capa-loja';
import { CabecalhoModal } from '../../components/cabecalho-modal';
import { StatusLoja } from '../../components/status-loja';
import { SeloNovo } from '../../components/selo-novo';
import { AvisoLojaFechada } from '../../components/aviso-loja-fechada'
import { GatilhoConta, MenuLateral } from '../../components/menu-lateral';
import { RodapeLoja } from '../../components/rodape-loja';
import { PedidoEmAndamento } from '../../components/pedido-em-andamento';
import { BannersCardapio } from '../../components/banners-cardapio'
import { EstadoErro } from '../../components/estado-mensagem';
import { rastrearAbriuCardapio } from '../../lib/tracking';
import { bannersDisponiveis } from '../../lib/banners'
import { produtoIndisponivel } from '../../components/catalogo-kiosk/passos';
import { CatalogoKiosk } from './kiosk';

/** Rótulo do tipo de pedido (a partir do identificador), exibido no rodapé. */
export function CatalogoPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: loja } = useGetLojaAtualQuery();
  const canal = loja ? canalDoIdentificador(loja.identificador) : undefined;
  const aparelhoFixo = loja
    ? ehDispositivoFixo(loja.identificador) || ehVitrine(loja.identificador)
    : false;
  const { data, isLoading, error, refetch } = useGetCatalogoQuery(canal!, {
    skip: !canal,
    pollingInterval: aparelhoFixo ? 60_000 : 0,
  });
  // Em paralelo com o catálogo: a faixa de loja fechada já chega montada, sem empurrar a lista.
  useGetHorariosLojaQuery(undefined, { skip: loja?.identificador.tipo !== 'DELIVERY' })
  const itensCarrinho = useSelector((s: RootState) => s.carrinho.itens);
  const [mostrarAdicionado, setMostrarAdicionado] = useState(false);
  const [menuAberto, setMenuAberto] = useState(false);
  const [buscaAberta, setBuscaAberta] = useState(false);
  const [promocoesAberta, setPromocoesAberta] = useState(false);
  /** Muda quando o operador salva a impressora: o localStorage não re-renderiza sozinho. */
  // Barra fixa no topo aparece quando o cabeçalho da loja sai da tela (scroll).
  const [barraVisivel, setBarraVisivel] = useState(false);
  // Abas de grupos: só existem junto da barra fixa (o cliente já está no meio da
  // lista). Guardam o grupo que está sob a barra e levam para o escolhido.
  const refListaCatalogo = useRef<ListaCatalogoRef>(null);
  const [grupoVisivel, setGrupoVisivel] = useState<number | null>(null);
  const refBarraFixa = useRef<HTMLDivElement>(null);
  // Altura real da barra (toolbar + abas): é o quanto ela cobre da lista, e o
  // desconto que o "rolar até o grupo" precisa aplicar para o título não ficar
  // escondido debaixo dela.
  const [alturaBarra, setAlturaBarra] = useState(0);
  useLayoutEffect(() => {
    if (!barraVisivel) return undefined;
    const medir = (): void => setAlturaBarra(refBarraFixa.current?.offsetHeight ?? 0);
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [barraVisivel]);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const refSentinela = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    if (!node) return;
    const obs = new IntersectionObserver(
      (entradas) => setBarraVisivel(!(entradas[0]?.isIntersecting ?? true)),
      { threshold: 0 },
    );
    obs.observe(node);
    observerRef.current = obs;
  }, []);

  // Feedback ao voltar da tela de produto após adicionar ao carrinho.
  useEffect(() => {
    if ((location.state as { adicionado?: boolean } | null)?.adicionado) {
      setMostrarAdicionado(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  // As promoções saíram da barra fixa e viraram item do menu lateral; o diálogo
  // continua morando aqui, e o menu o abre por evento.
  useEffect(() => onAbrirPromocoes(() => setPromocoesAberta(true)), []);

  // Rastreia "abriu cardápio" quando o catálogo da loja é exibido.
  useEffect(() => {
    if (loja && data) rastrearAbriuCardapio(loja.configuracao.nome);
  }, [loja, data]);

  if (!loja) return <></>;
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !data) {
    return (
      <EstadoErro
        titulo="Não foi possível carregar o cardápio"
        descricao="Verifique sua conexão e tente de novo. Se continuar, chame um atendente."
        aoTentarNovamente={() => void refetch()}
      />
    );
  }

  // Mesa/comanda exige o identificador PRINCIPAL configurado (a conta é chaveada
  // por ele). Sem isso o modo não funciona — bloqueia já na abertura do cardápio.
  if (contextoSalao(loja).principalNaoConfigurado) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Este cardápio de mesa/comanda ainda não está configurado: falta definir o
          <strong> identificador principal</strong>. Avise o responsável pela loja.
        </Alert>
      </Box>
    );
  }

  // Tablet de mesa: tela cheia com categorias na lateral, grade de produtos e
  // passos em wizard. O layout abaixo atende os canais de celular/desktop.
  // (O totem de pedidos tem app próprio, em pages/totem/pedidos.)
  if (loja.identificador.tipo === 'TABLET') {
    return (
      <CatalogoKiosk
        loja={loja}
        grupos={data.grupos}
        aoAbrirCarrinho={() => navigate('/carrinho')}
        // Preço mudou ou item saiu do cardápio: passa pelo carrinho, que corrige e avisa o cliente.
        aoFinalizar={() => navigate(carrinhoEmDia(itensCarrinho, data) ? '/checkout' : '/carrinho')}
      />
    );
  }

  const apenasVitrine = ehVitrine(loja.identificador);
  const ehDelivery = loja.identificador.tipo === 'DELIVERY';
  const freteGratisAPartirDe = loja.configuracao.freteGratisAPartirDe ?? null;
  const chipFreteGratis = ehDelivery && freteGratisAPartirDe !== null && (
    <Chip
      size="small"
      icon={<DeliveryDiningIcon />}
      label={freteGratisAPartirDe > 0 ? `Grátis acima de ${formatarBRL(freteGratisAPartirDe)}` : 'Entrega grátis'}
      color="primary"
      variant="outlined"
    />
  );
  const totalItens = itensCarrinho.reduce((s, i) => s + i.quantidade, 0);
  const subtotalCarrinho = itensCarrinho.reduce((s, i) => s + i.precoUnitario * i.quantidade, 0);
  const mostrarBarraCarrinho = totalItens > 0 && !apenasVitrine;
  const qtdPromocoes = new Set(data.grupos.flatMap((g) => g.produtos.filter((p) => p.emPromocao).map((p) => p.id))).size;
  const banners = bannersDisponiveis(loja.configuracao.banners, data.grupos, qtdPromocoes)

  function abrirBanner(banner: BannerCardapio): void {
    switch (banner.destino.tipo) {
      case 'PRODUTO':
        navigate(`/produto/${banner.destino.idProduto}`)
        return
      case 'GRUPO':
        refListaCatalogo.current?.irParaGrupo(banner.destino.idGrupo)
        return
      case 'PROMOCOES':
        setPromocoesAberta(true)
    }
  }

  // Chip fixo com o escopo do pedido: mesa/comanda (com número) ou vitrine
  // (somente visualização). Demais tipos não têm chip.
  const chipPedido: { icon: JSX.Element; label: string; color: 'primary' | 'default' } | null =
    loja.identificador.tipo === 'MESA'
      ? { icon: <TableRestaurantIcon />, label: `Mesa ${loja.identificador.numero ?? ''}`.trim(), color: 'primary' }
      : loja.identificador.tipo === 'COMANDA'
        ? { icon: <ReceiptLongIcon />, label: `Comanda ${loja.identificador.numero ?? ''}`.trim(), color: 'primary' }
        : loja.identificador.tipo === 'VITRINE'
          ? { icon: <VisibilityOutlinedIcon />, label: 'Somente visualização', color: 'default' }
          : null;

  return (
    <>
    <Slide in={barraVisivel} direction="down">
      <AppBar
        ref={refBarraFixa}
        position="fixed"
        color="default"
        elevation={0}
        sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
      >
        {/* Compacta no celular (a barra come tela de cardápio); com a altura
            normal do MUI no desktop, onde a versão `dense` fica apertada. */}
        <Toolbar variant="dense" sx={{ minHeight: { xs: 48, md: 64 } }}>
          <IconButton edge="start" aria-label="Abrir menu" onClick={() => setMenuAberto(true)}>
            <MenuIcon />
          </IconButton>
          <Typography variant="subtitle1" noWrap sx={{ flexGrow: 1, ml: 1, fontSize: { md: '1.15rem' } }}>
            {loja.configuracao.nome}
          </Typography>
          <IconButton aria-label="Buscar no cardápio" onClick={() => setBuscaAberta(true)}>
            <SearchIcon />
          </IconButton>
          {/* Login/conta só existe no delivery — os demais modos são anônimos. */}
          {ehDelivery && <GatilhoConta aoAbrir={() => setMenuAberto(true)} />}
        </Toolbar>

        {/* Grupos do cardápio. Um grupo só não é navegação — vira ruído. */}
        {data.grupos.length > 1 && (
          <Tabs
            value={grupoVisivel ?? data.grupos[0]!.id}
            onChange={(_e, id: number) => refListaCatalogo.current?.irParaGrupo(id)}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              minHeight: { xs: 44, md: 56 },
              borderTop: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: { xs: 44, md: 56 },
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { md: '1rem' },
                px: { md: 2.5 },
              },
            }}
          >
            {data.grupos.map((g) => (
              <Tab key={g.id} value={g.id} label={g.descricao} />
            ))}
          </Tabs>
        )}
      </AppBar>
    </Slide>
    <MenuLateral aberto={menuAberto} aoFechar={() => setMenuAberto(false)} />
    <Box
      sx={{
        background: fundoNeutro,
        minHeight: '100vh',
        '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
      }}
    >
    <Container maxWidth="lg" sx={{ pt: 2, pb: mostrarBarraCarrinho ? 14 : 8 }}>
      {/* Cabeçalho da loja — renderiza sempre, mesmo quando o canal não tem produtos. */}
      {loja.configuracao.urlCover && (
        <CapaLoja
          url={loja.configuracao.urlCover}
          sx={{ height: 160, borderRadius: 2, mb: 2 }}
        />
      )}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Avatar
          src={loja.configuracao.urlLogo ?? undefined}
          variant="rounded"
          sx={{
            width: 56,
            height: 56,
            // logo transparente não deve ganhar fundo cinza; o cinza fica só como
            // placeholder do ícone quando a loja não tem logo.
            bgcolor: loja.configuracao.urlLogo ? 'transparent' : 'action.hover',
            flexShrink: 0,
          }}
        >
          {!loja.configuracao.urlLogo && <StorefrontIcon />}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {/* Nome com a linha só para ele e quebrando em vez de cortar: com a nota
              ao lado, nomes médios viravam "Boteco Sisc..." no celular. A nota
              mora na faixa de métricas, abaixo. */}
          <Typography variant="h5" sx={{ overflowWrap: 'anywhere', lineHeight: 1.25 }}>
            {loja.configuracao.nome}
          </Typography>
          {ehDelivery && (
            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <StatusLoja />
              {chipFreteGratis && <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>{chipFreteGratis}</Box>}
            </Box>
          )}
        </Box>
        {/* Celular: busca vira ícone ao lado da conta, como na barra fixa do topo. */}
        <IconButton
          aria-label="Buscar no cardápio"
          onClick={() => setBuscaAberta(true)}
          sx={{ display: { xs: 'inline-flex', sm: 'none' }, color: 'text.secondary' }}
        >
          <SearchIcon />
        </IconButton>
        {/* Conta no cabeçalho, e não numa barra de ações à parte: a barra fixa do
            topo (onde o gatilho também vive) só aparece depois do scroll. */}
        {ehDelivery && <GatilhoConta aoAbrir={() => setMenuAberto(true)} />}
      </Stack>

      {chipFreteGratis && (
        <Box sx={{ display: { xs: 'block', sm: 'none' }, ml: 9, mt: -1, mb: 2 }}>{chipFreteGratis}</Box>
      )}

      {ehDelivery && <AvisoLojaFechada />}

      {/* Sentinela: quando sai da tela (scroll), a barra fixa do topo aparece. */}
      <Box ref={refSentinela} sx={{ height: '1px' }} aria-hidden />

      {/* Desktop: métricas, promoções e busca na MESMA linha, encostadas à
          direita. Celular: as métricas tomam a linha inteira (o `width: 100%`
          força a quebra) e as ações descem para a linha de baixo, ainda à
          direita. Sem media query em JS — só o wrap do flex. */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="flex-end"
        // `useFlexGap`: sem ele o Stack aplica o spacing como margin-left no
        // segundo filho, que a mantém ao quebrar de linha — era isso que deixava
        // o bloco de ações deslocado à direita no celular.
        useFlexGap
        sx={{ my: 3, flexWrap: 'wrap', rowGap: 1.5 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 0, justifyContent: { xs: 'center', sm: 'flex-start' } }}
        >
          <Box sx={{ flex: '0 1 auto', minWidth: 0 }}>
            <InfoLoja config={loja.configuracao} tipo={loja.identificador.tipo} />
          </Box>
          <BadgeAvaliacoes variante="inline" />
        </Stack>

        {/* Busca em campo só a partir do sm; no celular ela é o ícone do cabeçalho da loja. */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="stretch"
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        >
          {/* Fundo igual ao da página (e não `action.hover`, que dava cara de campo
              desabilitado): o que sinaliza campo aqui é a borda + o texto de
              placeholder, do mesmo jeito que um TextField vazio. */}
          <ButtonBase
            onClick={() => setBuscaAberta(true)}
            aria-label="Buscar no cardápio"
            sx={{
              flex: { xs: '1 1 0', sm: '0 0 auto' },
              width: { xs: 'auto', sm: 240, md: 280 },
              minWidth: 0,
              justifyContent: 'flex-start',
              gap: 1,
              px: 1.75,
              height: ALTURA_ACAO_LOJA,
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              color: 'text.secondary',
              transition: (theme) => theme.transitions.create('border-color'),
              '&:hover': { borderColor: 'text.primary' },
            }}
          >
            <SearchIcon fontSize="small" />
            <Typography variant="body2" noWrap>
              Buscar no cardápio
            </Typography>
          </ButtonBase>
        </Stack>
      </Stack>

      {/* Antes das promoções: quem já pediu volta ao cardápio e acha o pedido na hora. */}
      {ehDelivery && <PedidoEmAndamento idConfiguracao={loja.configuracao.id} />}

      <BannersCardapio banners={banners} aoAbrir={abrirBanner} />

      {qtdPromocoes > 0 && <FaixaPromocoes quantidade={qtdPromocoes} aoAbrir={() => setPromocoesAberta(true)} />}

      {apenasVitrine && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Você está no modo vitrine — pedidos não são aceitos por este canal.
        </Alert>
      )}

      <ListaCatalogo
        ref={refListaCatalogo}
        grupos={data.grupos}
        offsetTopo={barraVisivel ? alturaBarra : 0}
        aoMudarGrupoVisivel={setGrupoVisivel}
      />
    </Container>

    <BuscaProdutos aberto={buscaAberta} aoFechar={() => setBuscaAberta(false)} grupos={data.grupos} />

    <PromocoesProdutos aberto={promocoesAberta} aoFechar={() => setPromocoesAberta(false)} grupos={data.grupos} />

    {/* Rodapé padrão para TODOS os tipos (inclusive totem) — também hospeda o
        ícone discreto de configuração do totem (ConfigTotem). */}
    <Box sx={{ pb: mostrarBarraCarrinho ? 10 : 0 }}>
      <RodapeLoja config={loja.configuracao} tipo={loja.identificador.tipo} />
    </Box>
    </Box>

    {mostrarBarraCarrinho && (
      <Paper
        variant="outlined"
        sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, p: 1.5, zIndex: 3 }}
      >
        <Container maxWidth="lg" disableGutters>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Badge badgeContent={totalItens} color="primary">
              <ShoppingCartIcon />
            </Badge>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {totalItens} {totalItens === 1 ? 'item' : 'itens'}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1 }}>
                {formatarBRL(subtotalCarrinho)}
              </Typography>
            </Box>
            <Button variant="contained" size="large" onClick={() => navigate('/carrinho')}>
              Ver carrinho
            </Button>
          </Stack>
        </Container>
      </Paper>
    )}

    {/* Chip sempre visível com o escopo do pedido (mesa/comanda/vitrine) —
        canto inferior direito, acima da barra do carrinho para não atrapalhar. */}
    {chipPedido && (
      <Chip
        icon={chipPedido.icon}
        label={chipPedido.label}
        color={chipPedido.color}
        sx={{
          position: 'fixed',
          right: 16,
          bottom: mostrarBarraCarrinho ? 88 : 16,
          zIndex: 4,
          boxShadow: 3,
          fontWeight: 600,
        }}
      />
    )}

    <Snackbar
      open={mostrarAdicionado}
      autoHideDuration={2500}
      onClose={() => setMostrarAdicionado(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ bottom: { xs: mostrarBarraCarrinho ? 96 : 24 } }}
    >
      <Alert severity="success" variant="filled" onClose={() => setMostrarAdicionado(false)}>
        Item adicionado ao carrinho
      </Alert>
    </Snackbar>
    </>
  );
}

/**
 * Entrada das promoções: faixa acima dos destaques, no tom suave da cor da marca —
 * chama atenção sem competir com o campo de busca.
 */
function FaixaPromocoes({ quantidade, aoAbrir }: { quantidade: number; aoAbrir: () => void }): JSX.Element {
  return (
    <ButtonBase
      onClick={aoAbrir}
      sx={(tema) => ({
        width: '100%',
        mb: 3,
        px: 2,
        py: 1.5,
        gap: 1.5,
        justifyContent: 'flex-start',
        textAlign: 'left',
        borderRadius: 2,
        // Mesmo destaque do "Entrar ou cadastrar" do menu lateral: fundo suave na cor da marca.
        bgcolor: alpha(tema.palette.primary.main, 0.07),
        transition: tema.transitions.create('background-color'),
        '&:hover': { bgcolor: alpha(tema.palette.primary.main, 0.12) },
      })}
    >
      <Box sx={{ color: 'primary.main', display: 'flex', flexShrink: 0 }}>
        <LocalOfferIcon />
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 600 }}>
          Promoções
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {quantidade === 1 ? '1 item com preço especial' : `${quantidade} itens com preço especial`}
        </Typography>
      </Box>
      <ChevronRightIcon fontSize="small" sx={{ color: 'primary.main' }} />
    </ButtonBase>
  );
}

/** Altura do campo de busca. */
const ALTURA_ACAO_LOJA = 40;

/**
 * Estado vazio do cardápio: a loja já apareceu (capa/logo/nome acima); aqui só
 * sinalizamos, de forma amigável, que ainda não há produtos no canal atual.
 */
function CardapioVazio(): JSX.Element {
  return (
    <Box sx={{ textAlign: 'center', py: 8, px: 2, color: 'text.secondary' }}>
      <RestaurantMenuIcon sx={{ fontSize: 64, opacity: 0.4, mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        Cardápio chegando
      </Typography>
      <Typography variant="body2">
        Estamos preparando o cardápio desta loja. Volte em breve!
      </Typography>
    </Box>
  );
}

/**
 * Faixa de métricas da loja (tempo de retirada, tempo de entrega e pedido
 * mínimo), inspirada no cabeçalho do app. Cada item só aparece quando a
 * configuração tem o dado; o mínimo é sempre exibido (0 = "R$ 0,00").
 *
 * TOTEM mostra só a retirada: entrega não se aplica ao autoatendimento na
 * loja e pedido mínimo não é validado nesse canal.
 */
function InfoLoja({ config, tipo }: { config: ConfiguracaoLoja; tipo: IdentificadorLoja['tipo'] }): JSX.Element | null {
  const ehTotem = tipo === 'TOTEM';

  // Pedido mínimo do canal atual (delivery usa valorMinimoPedido). Só exibe
  // quando há mínimo configurado (> 0).
  const minimoBruto =
    tipo === 'TOTEM'
      ? config.valorMinimoTotem
      : tipo === 'MESA' || tipo === 'COMANDA'
        ? config.valorMinimoMesaComanda
        : config.valorMinimoPedido;
  const minimo = Number(minimoBruto ?? 0);

  // Memoizado: a página re-renderiza a cada rolagem (aba ativa do topo), e uma
  // lista nova a cada render remediria as métricas junto.
  const itens = useMemo<Metrica[]>(() => {
    const out: Metrica[] = [];
    if (config.tempoRetirada != null) {
      out.push({ chave: 'retirada', icone: <AccessTimeIcon />, valor: `${config.tempoRetirada}m`, rotulo: 'retirada' });
    }
    if (!ehTotem && config.tempoEntrega != null) {
      out.push({ chave: 'entrega', icone: <DeliveryDiningIcon />, valor: `${config.tempoEntrega}m`, rotulo: 'entrega' });
    }
    if (minimo > 0) {
      out.push({ chave: 'minimo', icone: <AttachMoneyIcon />, valor: formatarBRL(minimo), rotulo: 'mínimo' });
    }
    return out;
  }, [config.tempoRetirada, config.tempoEntrega, ehTotem, minimo]);

  if (itens.length === 0) return null;

  return <MetricasLoja itens={itens} />;
}

interface Metrica {
  chave: string;
  icone: ReactNode;
  valor: string;
  rotulo: string;
}

/** Largura reservada para o botão "ver mais" ao decidir o que cabe na linha. */
const LARGURA_VER_MAIS = 92;

/**
 * Métricas da loja numa linha só; no celular, o que não couber vai para o
 * "ver mais".
 *
 * O corte é medido, não estimado por breakpoint: o que cabe depende do conteúdo
 * (uma loja tem "R$ 20,00 mínimo", outra só "45m entrega") e da fonte do
 * aparelho. No desktop não há corte — a linha tem espaço e o flex-wrap do bloco
 * pai já resolve o resto.
 */
function MetricasLoja({ itens }: { itens: Metrica[] }): JSX.Element {
  const theme = useTheme();
  const compacto = useMediaQuery(theme.breakpoints.down('sm'));

  const refExterno = useRef<HTMLDivElement>(null);
  const refItens = useRef<HTMLDivElement>(null);
  const [visiveis, setVisiveis] = useState(itens.length);
  // Durante a medição TUDO é renderizado — item escondido não tem largura para
  // medir, e sem isso a linha nunca voltaria a crescer ao alargar a tela.
  const [medindo, setMedindo] = useState(true);
  const [ancora, setAncora] = useState<HTMLElement | null>(null);
  // Só o conteúdo importa para remedir; a identidade do array, não.
  const assinatura = itens.map((i) => `${i.chave}:${i.valor}`).join('|');

  // Observa o container EXTERNO, cuja largura é a da coluna e não muda quando
  // itens são escondidos. Observar a faixa interna (que encolhe ao surgir o
  // "ver mais") realimentaria a medição e ela ficaria oscilando.
  useLayoutEffect(() => {
    setMedindo(true);
    const el = refExterno.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const obs = new ResizeObserver(() => setMedindo(true));
    obs.observe(el);
    return () => obs.disconnect();
  }, [assinatura]);

  useLayoutEffect(() => {
    if (!medindo) return;
    if (!compacto) {
      setVisiveis(itens.length);
      setMedindo(false);
      return;
    }


    const externo = refExterno.current;
    const faixa = refItens.current;
    if (!externo || !faixa) return;

    const filhos = Array.from(faixa.children) as HTMLElement[];
    const gap = parseFloat(getComputedStyle(faixa).columnGap || '0') || 0;
    const disponivel = externo.clientWidth;

    let usado = 0;
    let cabem = 0;
    for (const filho of filhos) {
      const largura = usado + (cabem > 0 ? gap : 0) + filho.offsetWidth;
      if (largura > disponivel) break;
      usado = largura;
      cabem += 1;
    }

    // Sobrou item: o "ver mais" também precisa de lugar na linha.
    if (cabem < filhos.length) {
      while (cabem > 0 && usado + gap + LARGURA_VER_MAIS > disponivel) {
        const filho = filhos[cabem - 1]!;
        usado -= filho.offsetWidth + (cabem > 1 ? gap : 0);
        cabem -= 1;
      }
    }

    setVisiveis(cabem);
    setMedindo(false);
  }, [medindo, compacto, assinatura, itens.length]);

  const cortou = !medindo && visiveis < itens.length;

  return (
    <Stack
      ref={refExterno}
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ minWidth: 0, justifyContent: { xs: 'center', sm: 'flex-start' } }}
    >
      <Box
        ref={refItens}
        sx={{
          display: 'flex',
          alignItems: 'center',
          columnGap: { xs: 2, sm: 3 },
          flexWrap: 'nowrap',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        {itens.map((item, i) => (
          <Box
            key={item.chave}
            sx={{ flexShrink: 0, display: medindo || i < visiveis ? 'flex' : 'none' }}
          >
            <InfoItem icone={item.icone} valor={item.valor} rotulo={item.rotulo} />
          </Box>
        ))}
      </Box>

      {cortou && (
        <Button
          size="small"
          onClick={(e) => setAncora(e.currentTarget)}
          sx={{ flexShrink: 0, textTransform: 'none' }}
        >
          ver mais
        </Button>
      )}

      <Popover
        open={Boolean(ancora)}
        anchorEl={ancora}
        onClose={() => setAncora(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack spacing={1.5} sx={{ p: 2 }}>
          {itens.map((item) => (
            <InfoItem key={item.chave} icone={item.icone} valor={item.valor} rotulo={item.rotulo} />
          ))}
        </Stack>
      </Popover>
    </Stack>
  );
}

/** Métrica da loja em UMA linha: ícone, valor e rótulo lado a lado. */
function InfoItem({ icone, valor, rotulo }: { icone: ReactNode; valor: string; rotulo: string }): JSX.Element {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'text.secondary' }}>
      {icone}
      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1 }} noWrap>
        {valor}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1 }} noWrap>
        {rotulo}
      </Typography>
    </Stack>
  );
}

/**
 * Modal de busca de produtos por nome/descrição. Abre a partir do botão de
 * busca no cabeçalho. Os resultados reaproveitam o {@link CardProduto} (mesmo
 * visual e navegação); ao escolher um, o modal fecha e abre o detalhe.
 * Responsivo: tela cheia no mobile, modal centralizado no desktop.
 */
function BuscaProdutos({
  aberto,
  aoFechar,
  grupos,
}: {
  aberto: boolean;
  aoFechar: () => void;
  grupos: GrupoCatalogo[];
}): JSX.Element {
  const theme = useTheme();
  const ehDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [termo, setTermo] = useState('');
  // Foca o campo após a transição de abertura (autoFocus sozinho é instável
  // dentro do Dialog por causa do focus trap).
  const inputRef = useRef<HTMLInputElement>(null);

  // Lista única de produtos (um produto pode estar em mais de um grupo).
  const produtos = useMemo(() => {
    const vistos = new Set<number>();
    const lista: ProdutoCatalogo[] = [];
    for (const g of grupos) {
      for (const p of g.produtos) {
        if (!vistos.has(p.id)) {
          vistos.add(p.id);
          lista.push(p);
        }
      }
    }
    return lista;
  }, [grupos]);

  const busca = normalizarTexto(termo.trim());
  const resultados = useMemo(() => {
    if (!busca) return [];
    return produtos.filter((p) =>
      normalizarTexto(
        `${p.descricao} ${p.descricaoComposicao ?? ''} ${p.descricaoCompleta ?? ''} ${p.descricaoSucinta ?? ''}`,
      ).includes(busca),
    );
  }, [produtos, busca]);

  // Fecha sempre zerando o termo, para reabrir limpo.
  const fechar = (): void => {
    setTermo('');
    aoFechar();
  };

  return (
    <Dialog
      open={aberto}
      onClose={fechar}
      fullScreen={!ehDesktop}
      fullWidth
      maxWidth="md"
      scroll="paper"
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
      PaperProps={{ sx: { ...(ehDesktop ? { height: '90vh' } : {}) } }}
    >
      <CabecalhoModal
        aoFechar={fechar}
        acao={
          termo ? (
            <Button size="small" color="inherit" onClick={() => setTermo('')}>
              Limpar
            </Button>
          ) : undefined
        }
      >
        <TextField
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          placeholder="Buscar no cardápio"
          fullWidth
          autoFocus
          inputRef={inputRef}
          variant="standard"
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </CabecalhoModal>

      <DialogContent sx={{ p: 2 }}>
        {!busca ? (
          <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
            <SearchIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1 }} />
            <Typography variant="body2">Digite para buscar produtos pelo nome ou descrição.</Typography>
          </Box>
        ) : resultados.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
            <Typography variant="body2">Nenhum produto encontrado para “{termo.trim()}”.</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            {resultados.map((p) => (
              <CardProduto key={p.id} produto={p} aoNavegar={fechar} />
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Modal com os produtos em promoção (campo `emPromocao`). Mesmo visual do modal
 * de busca, mas sem campo de texto: lista direto os produtos marcados. Ao
 * escolher um, fecha e abre o detalhe. Responsivo (tela cheia no mobile).
 */
function PromocoesProdutos({
  aberto,
  aoFechar,
  grupos,
}: {
  aberto: boolean;
  aoFechar: () => void;
  grupos: GrupoCatalogo[];
}): JSX.Element {
  const theme = useTheme();
  const ehDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Produtos em promoção (únicos — um produto pode estar em mais de um grupo).
  const promocoes = useMemo(() => {
    const vistos = new Set<number>();
    const lista: ProdutoCatalogo[] = [];
    for (const g of grupos) {
      for (const p of g.produtos) {
        if (p.emPromocao && !vistos.has(p.id)) {
          vistos.add(p.id);
          lista.push(p);
        }
      }
    }
    return lista;
  }, [grupos]);

  return (
    <Dialog
      open={aberto}
      onClose={aoFechar}
      fullScreen={!ehDesktop}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{ sx: { ...(ehDesktop ? { height: '90vh' } : {}) } }}
    >
      <CabecalhoModal titulo="Promoções" aoFechar={aoFechar} inicio={<LocalOfferIcon color="error" />} />

      <DialogContent sx={{ p: 2 }}>
        {promocoes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
            <LocalOfferIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1 }} />
            <Typography variant="body2">Nenhum produto em promoção no momento.</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            {promocoes.map((p) => (
              <CardProduto key={p.id} produto={p} aoNavegar={aoFechar} />
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Seção "Destaques" no topo do cardápio (estilo iFood): junta os produtos
 * marcados como `destaqueCardapio` de todos os grupos e os exibe num carrossel
 * horizontal de cards verticais (imagem em cima, preço e nome embaixo).
 */
type LinhaCatalogo =
  | { tipo: 'destaques'; chave: string }
  | { tipo: 'header'; chave: string; descricao: string; idGrupo: number }
  | { tipo: 'produtos'; chave: string; produtos: ProdutoCatalogo[] };

/**
 * Lista do catálogo VIRTUALIZADA: só as linhas visíveis na viewport entram no
 * DOM. Com 100+ produtos isso mantém a árvore pequena — elimina a lentidão ao
 * abrir/fechar os modais (o scroll-lock/reflow do MUI passa a operar sobre
 * poucos nós) e deixa a rolagem fluida. Memoizada para não reconciliar quando a
 * CatalogoPage re-renderiza nas navegações.
 */
/** Comandos que a página dispara na lista (as abas do topo). */
export interface ListaCatalogoRef {
  /** Rola até o cabeçalho do grupo, logo abaixo da barra fixa. */
  irParaGrupo: (idGrupo: number) => void;
}

const ListaCatalogo = memo(
  forwardRef<
    ListaCatalogoRef,
    {
      grupos: GrupoCatalogo[];
      /** Altura da barra fixa: o que ela cobre da lista. */
      offsetTopo?: number;
      /** Grupo que está sob a barra fixa — alimenta a aba ativa. */
      aoMudarGrupoVisivel?: (idGrupo: number) => void;
    }
  >(function ListaCatalogo({ grupos, offsetTopo = 0, aoMudarGrupoVisivel }, ref): JSX.Element {
  const theme = useTheme();
  const acimaSm = useMediaQuery(theme.breakpoints.up('sm'));
  const acimaMd = useMediaQuery(theme.breakpoints.up('md'));
  const acimaLg = useMediaQuery(theme.breakpoints.up('lg'));
  // Colunas por breakpoint — espelha o antigo Grid (xs:1, sm:2, md:3, lg:4).
  const colunas = acimaLg ? 4 : acimaMd ? 3 : acimaSm ? 2 : 1;

  const temDestaques = useMemo(
    () => grupos.some((g) => g.produtos.some((p) => p.destaqueCardapio)),
    [grupos],
  );

  // Achata grupos/produtos numa lista linear: cada grupo vira 1 cabeçalho + N
  // linhas de até `colunas` produtos. Assim o virtualizador opera linha a linha.
  const linhas = useMemo<LinhaCatalogo[]>(() => {
    const out: LinhaCatalogo[] = [];
    if (temDestaques) out.push({ tipo: 'destaques', chave: 'destaques' });
    for (const g of grupos) {
      out.push({ tipo: 'header', chave: `h-${g.id}`, descricao: g.descricao, idGrupo: g.id });
      for (let i = 0; i < g.produtos.length; i += colunas) {
        out.push({ tipo: 'produtos', chave: `g-${g.id}-${i}`, produtos: g.produtos.slice(i, i + colunas) });
      }
    }
    return out;
  }, [grupos, colunas, temDestaques]);

  const refLista = useRef<HTMLDivElement>(null);
  // Distância do topo do documento até o início da lista (capa/logo/infos acima).
  // O virtualizador de janela precisa dela como scrollMargin para posicionar.
  const [margemTopo, setMargemTopo] = useState(0);
  useLayoutEffect(() => {
    const medir = (): void => setMargemTopo(refLista.current?.offsetTop ?? 0);
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, []);

  const virtualizer = useWindowVirtualizer({
    count: linhas.length,
    estimateSize: () => 160,
    overscan: 6,
    scrollMargin: margemTopo,
    // O quanto a barra fixa cobre: entra no cálculo do `scrollToIndex`, então o
    // cabeçalho do grupo para logo ABAIXO dela, e não escondido atrás.
    scrollPaddingStart: offsetTopo,
    getItemKey: (i) => linhas[i]?.chave ?? `l-${i}`,
  });

  // Índice de cada cabeçalho de grupo dentro da lista achatada.
  const indicePorGrupo = useMemo(() => {
    const mapa = new Map<number, number>();
    linhas.forEach((linha, i) => {
      if (linha.tipo === 'header') mapa.set(linha.idGrupo, i);
    });
    return mapa;
  }, [linhas]);

  // Enquanto o scroll programático corre, o spy fica quieto: os grupos do caminho
  // passariam pela barra e a aba ativa piscaria até chegar no destino.
  const rolandoPara = useRef<number | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      irParaGrupo: (idGrupo: number) => {
        const indice = indicePorGrupo.get(idGrupo);
        if (indice === undefined) return;

        rolandoPara.current = idGrupo;
        aoMudarGrupoVisivel?.(idGrupo);

        // `scrollToIndex` do virtualizador, e não um `window.scrollTo` calculado:
        // as linhas do caminho ainda estão com altura ESTIMADA, e ele reconcilia o
        // destino conforme elas são medidas de verdade. No cálculo manual o erro
        // acumula e grupo distante para longe do lugar.
        virtualizer.scrollToIndex(indice, { align: 'start', behavior: 'smooth' });

        // Solta o spy depois da rolagem (inclusive da reconciliação).
        window.setTimeout(() => {
          rolandoPara.current = null;
        }, 900);
      },
    }),
    [indicePorGrupo, virtualizer, aoMudarGrupoVisivel],
  );

  // Grupo sob a barra fixa: o último cabeçalho que já passou por ela.
  //
  // Lê `measurementsCache` (posição real de cada linha, sem o clamp que o
  // `getOffsetForIndex` aplica perto do fim da lista) e recalcula a cada rolagem
  // — o virtualizador re-renderiza no scroll, então não é preciso listener.
  const grupoSobABarra = useMemo(() => {
    const limite = (virtualizer.scrollOffset ?? 0) + offsetTopo + 1;
    let atual: number | null = null;
    for (const linha of linhas) {
      if (linha.tipo !== 'header') continue;
      const inicio = virtualizer.measurementsCache[indicePorGrupo.get(linha.idGrupo) ?? -1]?.start;
      if (inicio === undefined || inicio > limite) break;
      atual = linha.idGrupo;
    }
    // Antes do primeiro cabeçalho (destaques/topo), a primeira aba é a ativa.
    return atual ?? (linhas.find((l) => l.tipo === 'header') as { idGrupo: number } | undefined)?.idGrupo ?? null;
    // `scrollOffset` muda a cada rolagem e é o que dispara o recálculo.
  }, [virtualizer.scrollOffset, virtualizer, linhas, indicePorGrupo, offsetTopo]);

  useEffect(() => {
    if (rolandoPara.current !== null || grupoSobABarra === null) return;
    aoMudarGrupoVisivel?.(grupoSobABarra);
  }, [grupoSobABarra, aoMudarGrupoVisivel]);

  if (grupos.length === 0) return <CardapioVazio />;

  return (
    <Box ref={refLista} sx={{ position: 'relative', width: '100%', height: virtualizer.getTotalSize() }}>
      {virtualizer.getVirtualItems().map((vi) => {
        const linha = linhas[vi.index];
        if (!linha) return null;
        return (
          <Box
            key={vi.key}
            data-index={vi.index}
            ref={virtualizer.measureElement}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${vi.start - virtualizer.options.scrollMargin}px)`,
            }}
          >
            {/* pb dá o respiro vertical entre as linhas (entra na medição). */}
            <Box sx={{ pb: 2 }}>
              {linha.tipo === 'destaques' && <SecaoDestaques grupos={grupos} />}
              {linha.tipo === 'header' && (
                <Typography variant="h6" sx={{ mb: 1, pt: 1 }}>
                  {linha.descricao}
                </Typography>
              )}
              {linha.tipo === 'produtos' && (
                <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${colunas}, minmax(0, 1fr))`, gap: 2 }}>
                  {linha.produtos.map((produto) => (
                    <CardProduto key={produto.id} produto={produto} />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
  }),
);

function SecaoDestaques({ grupos }: { grupos: GrupoCatalogo[] }): JSX.Element | null {
  // Mantém a ordem dos grupos, mas remove eventual produto repetido entre grupos.
  const vistos = new Set<number>();
  const destaques: ProdutoCatalogo[] = [];
  for (const produto of grupos.flatMap((g) => g.produtos)) {
    if (produto.destaqueCardapio && !vistos.has(produto.id)) {
      vistos.add(produto.id);
      destaques.push(produto);
    }
  }

  if (destaques.length === 0) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Destaques
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          overflowX: 'auto',
          pb: 1,
          // Carrossel "encaixa" cada card ao parar de arrastar; some a scrollbar.
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {destaques.map((produto) => (
          <Box
            key={produto.id}
            sx={{
              flex: '0 0 auto',
              width: { xs: 150, sm: 170 },
              scrollSnapAlign: 'start',
            }}
          >
            <CardDestaque produto={produto} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function CardDestaque({ produto }: { produto: ProdutoCatalogo }): JSX.Element {
  const navigate = useNavigate();
  const imagem = produto.imagens[0];
  // Estilo iFood: foto numa caixa cinza-clara arredondada (com respiro), e o
  // texto/preço logo abaixo, soltos — sem moldura/card em volta.
  const conteudo = (
    <>
      <Box sx={{ position: 'relative', mb: 1 }}>
        {imagem ? (
          <CardMedia
            component="img"
            image={urlImagemProduto(imagem, { largura: 320, altura: 320, modo: 'cover' })}
            alt={produto.descricao}
            sx={{
              width: '100%',
              aspectRatio: '1 / 1',
              objectFit: 'cover',
              objectPosition: 'center',
              bgcolor: 'grey.100',
              borderRadius: 3,
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              aspectRatio: '1 / 1',
              borderRadius: 3,
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <RestaurantMenuIcon color="disabled" />
          </Box>
        )}
        {produto.emPromocao && (
          <Chip
            icon={<LocalOfferIcon />}
            label="Promo"
            color="error"
            size="small"
            sx={{ position: 'absolute', top: 8, right: 8 }}
          />
        )}
      </Box>
      <PrecoProduto produto={produto} valor={{ variant: 'subtitle1', sx: { fontWeight: 700 } }} />
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {capitalize(produto.descricao)}
      </Typography>
      {produto.novo && <SeloNovo emLinhaPropria />}
    </>
  );

  return (
    <ButtonBase
      onClick={() => navigate(`/produto/${produto.id}`)}
      sx={{ display: 'block', width: '100%', textAlign: 'left', borderRadius: 3 }}
    >
      {conteudo}
    </ButtonBase>
  );
}

/** Limite de caracteres da composição no card (o resto é cortado com reticências). */
const LIMITE_COMPOSICAO = 90;

function CardProduto({ produto, aoNavegar }: { produto: ProdutoCatalogo; aoNavegar?: () => void }): JSX.Element {
  const navigate = useNavigate();
  const irParaProduto = (): void => {
    aoNavegar?.();
    navigate(`/produto/${produto.id}`);
  };
  const imagem = produto.imagens[0];
  const composicao = produto.descricaoComposicao?.trim();
  const composicaoCurta =
    composicao && composicao.length > LIMITE_COMPOSICAO
      ? `${composicao.slice(0, LIMITE_COMPOSICAO).trimEnd()}…`
      : composicao;
  const conteudo = (
    <Stack direction="row" spacing={1.5} alignItems="stretch" sx={{ width: '100%', height: '100%' }}>
      <CardContent sx={{ flexGrow: 1, py: 1.5, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            lineHeight: 1.25,
            // Cor sóbria (não preto puro); adapta no dark.
            color: (theme) => (theme.palette.mode === 'light' ? '#404040' : theme.palette.text.primary),
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {capitalize(produto.descricao)}
        </Typography>
        {produto.novo && <SeloNovo emLinhaPropria />}
        {composicaoCurta && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {composicaoCurta}
          </Typography>
        )}
        {/* Preço ancorado na base; um tom mais claro e sem negrito p/ diferenciar do nome. */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 'auto', pt: 1, flexWrap: 'wrap' }}>
          <PrecoProduto produto={produto} valor={{ variant: 'subtitle1', color: 'text.secondary' }} />
          {/* Sem imagem: mantém o "Promo" ao lado do preço (não há onde sobrepor). */}
          {produto.emPromocao && !imagem && (
            <Chip icon={<LocalOfferIcon />} label="Promo" color="error" size="small" />
          )}
          {/* Passo obrigatório com todas as opções pausadas: avisa já na lista, para o cliente não
              abrir o produto só para descobrir que não dá. O toque continua levando à tela do
              produto — é lá que está o motivo e o contato da loja. */}
          {produtoIndisponivel(produto) && (
            <Chip label="Indisponível" color="warning" variant="outlined" size="small" />
          )}
        </Stack>
      </CardContent>
      {imagem && (
        // Foto em `cover` encostada na borda direita, ocupando toda a altura do
        // card. Os cantos externos são clipados pelo próprio Card (overflow:hidden).
        <Box sx={{ position: 'relative', width: 112, flexShrink: 0, alignSelf: 'stretch' }}>
          <CardMedia
            component="img"
            image={urlImagemProduto(imagem, { largura: 240, altura: 240, modo: 'cover' })}
            alt={produto.descricao}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          {produto.emPromocao && (
            <Chip
              icon={<LocalOfferIcon />}
              label="Promo"
              color="error"
              size="small"
              sx={{ position: 'absolute', top: 8, right: 8 }}
            />
          )}
        </Box>
      )}
    </Stack>
  );
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardActionArea onClick={irParaProduto} sx={{ height: '100%' }}>
        {conteudo}
      </CardActionArea>
    </Card>
  );
}
